import React, { useState, useEffect, useRef } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { 
  Send, 
  Paperclip, 
  FileText, 
  User, 
  Clock, 
  Search,
  ChevronLeft,
  X,
  Download,
  Filter
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { toast } from 'react-hot-toast';

// Mock data for conversations
const mockConversations = [
  {
    id: '1',
    orderId: 'ORD-123456',
    clientName: 'Sophie Dupont',
    clientEmail: 'sophie.dupont@example.com',
    contractName: 'Freelance Overeenkomst',
    lastMessage: 'Votre contrat est prêt. Vous pouvez le télécharger dès maintenant.',
    lastMessageDate: '2023-06-20T14:30:00',
    unread: false,
    messages: [
      {
        id: '1-1',
        sender: 'lawyer',
        senderName: 'Me. Sophie Martin',
        content: 'Bonjour, je suis Me. Sophie Martin, en charge de la préparation de votre contrat de freelance. N\'hésitez pas à me contacter si vous avez des questions.',
        timestamp: '2023-06-16T10:15:00',
        read: true,
      },
      {
        id: '1-2',
        sender: 'client',
        senderName: 'Sophie Dupont',
        content: 'Bonjour Me. Martin, merci pour votre message. J\'aurais une question concernant la clause de propriété intellectuelle. Est-ce que je conserve les droits sur les créations réalisées dans le cadre de ma mission ?',
        timestamp: '2023-06-16T11:30:00',
        read: true,
      },
      {
        id: '1-3',
        sender: 'lawyer',
        senderName: 'Me. Sophie Martin',
        content: 'Bonjour, dans la version standard du contrat, les droits de propriété intellectuelle sont cédés au client. Cependant, nous pouvons modifier cette clause selon vos besoins. Souhaitez-vous conserver certains droits ou limiter la cession d\'une manière spécifique ?',
        timestamp: '2023-06-16T14:45:00',
        read: true,
      },
      {
        id: '1-4',
        sender: 'client',
        senderName: 'Sophie Dupont',
        content: 'Je souhaiterais conserver le droit d\'utiliser les créations dans mon portfolio et pour des présentations professionnelles. Est-ce possible ?',
        timestamp: '2023-06-17T09:20:00',
        read: true,
      },
      {
        id: '1-5',
        sender: 'lawyer',
        senderName: 'Me. Sophie Martin',
        content: 'Tout à fait, c\'est une demande courante et raisonnable. J\'ai ajouté une clause spécifique permettant l\'utilisation des créations dans votre portfolio et pour des présentations professionnelles, tout en maintenant la cession des droits d\'exploitation commerciale au client. Le contrat mis à jour est en cours de finalisation.',
        timestamp: '2023-06-17T11:05:00',
        read: true,
      },
      {
        id: '1-6',
        sender: 'lawyer',
        senderName: 'Me. Sophie Martin',
        content: 'Votre contrat est prêt. Vous pouvez le télécharger dès maintenant.',
        timestamp: '2023-06-20T14:30:00',
        read: true,
        attachment: {
          name: 'Freelance_Overeenkomst_Final.pdf',
          size: '1.2 MB',
        },
      },
    ],
  },
  {
    id: '2',
    orderId: 'ORD-234567',
    clientName: 'Thomas Laurent',
    clientEmail: 'thomas.laurent@example.com',
    contractName: 'Geheimhoudingsovereenkomst (NDA)',
    lastMessage: 'J\'ai besoin de quelques précisions supplémentaires concernant la nature des informations confidentielles.',
    lastMessageDate: '2023-06-24T16:45:00',
    unread: true,
    messages: [
      {
        id: '2-1',
        sender: 'lawyer',
        senderName: 'Me. Thomas Dubois',
        content: 'Bonjour, je suis Me. Thomas Dubois, en charge de la préparation de votre accord de confidentialité. J\'ai examiné vos réponses au questionnaire et j\'aurais besoin de quelques précisions supplémentaires.',
        timestamp: '2023-06-23T15:30:00',
        read: true,
      },
      {
        id: '2-2',
        sender: 'lawyer',
        senderName: 'Me. Thomas Dubois',
        content: 'J\'ai besoin de quelques précisions supplémentaires concernant la nature des informations confidentielles. Pourriez-vous détailler davantage les types de données techniques qui seront partagées ?',
        timestamp: '2023-06-24T16:45:00',
        read: false,
      },
    ],
  },
  {
    id: '3',
    orderId: 'ORD-345678',
    clientName: 'Marie Fontaine',
    clientEmail: 'marie.fontaine@example.com',
    contractName: 'Arbeidsovereenkomst',
    lastMessage: 'Nous avons bien reçu votre commande. Veuillez remplir le questionnaire pour que nous puissions préparer votre contrat.',
    lastMessageDate: '2023-06-25T10:15:00',
    unread: false,
    messages: [
      {
        id: '3-1',
        sender: 'lawyer',
        senderName: 'Me. Julie Leroy',
        content: 'Bonjour Mme Fontaine, nous avons bien reçu votre commande. Veuillez remplir le questionnaire pour que nous puissions préparer votre contrat.',
        timestamp: '2023-06-25T10:15:00',
        read: true,
      },
    ],
  },
];

const AdminMessages: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialOrderId = queryParams.get('order');
  
  const [conversations, setConversations] = useState<any[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [isMobileListVisible, setIsMobileListVisible] = useState(true);
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Fetch conversations
  useEffect(() => {
    // In a real app, this would be an API call
    const fetchConversations = async () => {
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setConversations(mockConversations);
        
        // If order ID is provided in URL, select that conversation
        if (initialOrderId) {
          const conversation = mockConversations.find(c => c.orderId === initialOrderId);
          if (conversation) {
            setSelectedConversation(conversation);
            setIsMobileListVisible(false);
          }
        }
      } catch (error) {
        console.error('Error fetching conversations:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConversations();
  }, [initialOrderId]);
  
  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConversation]);
  
  // Mark messages as read when conversation is selected
  useEffect(() => {
    if (selectedConversation && selectedConversation.unread) {
      // In a real app, this would be an API call to mark messages as read
      setConversations(prevConversations => 
        prevConversations.map(conv => 
          conv.id === selectedConversation.id 
            ? { ...conv, unread: false, messages: conv.messages.map((msg: any) => ({ ...msg, read: true })) } 
            : conv
        )
      );
      
      setSelectedConversation(prev => 
        prev ? { ...prev, unread: false, messages: prev.messages.map((msg: any) => ({ ...msg, read: true })) } : null
      );
    }
  }, [selectedConversation]);
  
  const handleSelectConversation = (conversation: any) => {
    setSelectedConversation(conversation);
    setIsMobileListVisible(false);
  };
  
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim() || !selectedConversation) return;
    
    const newMessage = {
      id: `${selectedConversation.id}-${selectedConversation.messages.length + 1}`,
      sender: 'lawyer',
      senderName: 'Me. Sophie Martin', // In a real app, this would be the current user's name
      content: message,
      timestamp: new Date().toISOString(),
      read: true,
    };
    
    // Update conversations state
    setConversations(prevConversations => 
      prevConversations.map(conv => 
        conv.id === selectedConversation.id 
          ? { 
              ...conv, 
              messages: [...conv.messages, newMessage],
              lastMessage: message,
              lastMessageDate: new Date().toISOString(),
            } 
          : conv
      )
    );
    
    // Update selected conversation
    setSelectedConversation(prev => 
      prev ? { 
        ...prev, 
        messages: [...prev.messages, newMessage],
        lastMessage: message,
        lastMessageDate: new Date().toISOString(),
      } : null
    );
    
    // Clear input
    setMessage('');
    
    // In a real app, this would be an API call to send the message
    toast.success('Message envoyé');
  };
  
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  const handleStatusFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
  };
  
  const handleBackToList = () => {
    setIsMobileListVisible(true);
  };
  
  // Filter conversations based on search term and status
  const filteredConversations = conversations.filter(conv => {
    // Search filter
    const searchMatch = 
      conv.orderId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.clientName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.clientEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      conv.contractName.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Status filter (unread/all)
    const statusMatch = statusFilter === 'unread' ? conv.unread : true;
    
    return searchMatch && statusMatch;
  });
  
  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }
  
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Messagerie</h1>
      </div>
      
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="flex h-[600px]">
          {/* Conversation List - Hidden on mobile when a conversation is selected */}
          <div className={`w-full md:w-1/3 border-r border-gray-200 ${isMobileListVisible ? 'block' : 'hidden md:block'}`}>
            <div className="p-4 border-b border-gray-200">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="relative flex-grow">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                </div>
                
                <div className="flex items-center">
                  <Filter className="h-5 w-5 text-gray-400 mr-2" />
                  <select
                    className="block w-full border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                  >
                    <option value="">Tous les messages</option>
                    <option value="unread">Non lus</option>
                  </select>
                </div>
              </div>
            </div>
            
            <div className="overflow-y-auto h-[calc(600px-73px)]">
              {filteredConversations.length === 0 ? (
                <div className="p-4 text-center text-gray-500">
                  Aucune conversation trouvée
                </div>
              ) : (
                <ul className="divide-y divide-gray-200">
                  {filteredConversations.map((conversation) => (
                    <li 
                      key={conversation.id}
                      className={`hover:bg-gray-50 cursor-pointer ${selectedConversation?.id === conversation.id ? 'bg-blue-50' : ''}`}
                      onClick={() => handleSelectConversation(conversation)}
                    >
                      <div className="p-4 relative">
                        <div className="flex justify-between">
                          <h3 className="text-sm font-medium text-gray-900">{conversation.orderId}</h3>
                          <span className="text-xs text-gray-500">
                            {format(new Date(conversation.lastMessageDate), 'dd/MM/yyyy')}
                          </span>
                        </div>
                        <p className="text-sm font-medium text-gray-800 mt-1">{conversation.clientName}</p>
                        <p className="text-sm text-gray-600 mt-1">{conversation.contractName}</p>
                        <p className="text-sm text-gray-500 mt-1 truncate">{conversation.lastMessage}</p>
                        
                        {conversation.unread && (
                          <span className="absolute top-4 right-4 h-3 w-3 bg-blue-600 rounded-full"></span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          
          {/* Conversation Detail */}
          <div className={`w-full md:w-2/3 flex flex-col ${!isMobileListVisible ? 'block' : 'hidden md:flex'}`}>
            {selectedConversation ? (
              <>
                <div className="p-4 border-b border-gray-200 flex items-center">
                  <button
                    onClick={handleBackToList}
                    className="md:hidden text-gray-500 hover:text-gray-700 mr-2"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  
                  <div className="flex-grow">
                    <h3 className="text-lg font-medium text-gray-900">{selectedConversation.orderId}</h3>
                    <div className="flex items-center text-sm text-gray-600">
                      <User className="h-4 w-4 mr-1" />
                      {selectedConversation.clientName} ({selectedConversation.clientEmail})
                    </div>
                    <p className="text-sm text-gray-600">{selectedConversation.contractName}</p>
                  </div>
                  
                  <Link
                    to={`/admin/orders?search=${selectedConversation.orderId}`}
                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                  >
                    Voir la commande
                  </Link>
                </div>
                
                <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                  <div className="space-y-4">
                    {selectedConversation.messages.map((msg: any) => (
                      <div 
                        key={msg.id} 
                        className={`flex ${msg.sender === 'lawyer' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-xs md:max-w-md rounded-lg p-4 ${
                            msg.sender === 'lawyer' 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-white text-gray-800 border border-gray-200'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-1">
                            <span className="font-medium text-sm">{msg.senderName}</span>
                            <span className="text-xs opacity-75 ml-2">
                              {format(new Date(msg.timestamp), 'HH:mm')}
                            </span>
                          </div>
                          
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          
                          {msg.attachment && (
                            <div className={`mt-2 p-2 rounded-md flex items-center ${
                              msg.sender === 'lawyer' ? 'bg-blue-700' : 'bg-gray-100'
                            }`}>
                              <FileText className={`h-5 w-5 ${
                                msg.sender === 'lawyer' ? 'text-blue-200' : 'text-blue-600'
                              } mr-2`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium truncate ${
                                  msg.sender === 'lawyer' ? 'text-white' : 'text-gray-900'
                                }`}>
                                  {msg.attachment.name}
                                </p>
                                <p className={`text-xs ${
                                  msg.sender === 'lawyer' ? 'text-blue-200' : 'text-gray-500'
                                }`}>
                                  {msg.attachment.size}
                                </p>
                              </div>
                              <button className={`${
                                msg.sender === 'lawyer' ? 'text-white hover:text-blue-200' : 'text-blue-600 hover:text-blue-800'
                              }`}>
                                <Download className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </div>
                
                <div className="p-4 border-t border-gray-200 bg-white">
                  <form onSubmit={handleSendMessage} className="flex items-center">
                    <button
                      type="button"
                      className="text-gray-500 hover:text-gray-700 mr-2"
                      title="Joindre un fichier"
                    >
                      <Paperclip className="h-5 w-5" />
                    </button>
                    
                    <input
                      type="text"
                      placeholder="Écrivez votre message..."
                      className="flex-1 border border-gray-300 rounded-md py-2 px-3 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                    
                    <button
                      type="submit"
                      disabled={!message.trim()}
                      className="ml-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md p-2 disabled:opacity-50"
                    >
                      <Send className="h-5 w-5" />
                    </button>
                  </form>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-8 bg-gray-50">
                <FileText className="h-16 w-16 text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Aucune conversation sélectionnée</h3>
                <p className="text-gray-600 text-center max-w-md mb-6">
                  Sélectionnez une conversation dans la liste pour afficher les messages.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMessages;